import { IPlace } from 'models/address';
import { GenderTypes } from 'utils/enums/common';
import { IDeleteFilters, IGetSimpleListFilters, IGetSimpleListResponse, IPaginateResponse, ISearchWithPaginationFilter } from './common';
import { Tag } from 'models/general';
import { IPlaceResponse } from './general';

// ================= Utils Types =================

export type PassengerAddressesResponse = {
  base?: IPlaceResponse;
  occupation?: IPlaceResponse;
  alternate?: IPlaceResponse;
};

// ================= ENUM =================

export enum PassengerTypes {
  Student = 'student',
  Caregiver = 'caregiver',
  Default = 'default',
  Employee = 'employee',
}

// ================= REQUEST =================

export type IDeletePassengersFilters = IDeleteFilters & { types?: PassengerTypes[]; ids?: number[] };

export type IPassengersQueryFilters = {
  types?: PassengerTypes[];
  ids?: number[];
  excludeIds?: number[];
  profileId?: number;
  profiles?: Array<number | string | null>;
  attributes?: Array<number | string | null>;
  occupationIds?: number[];
};

export type IGetPassengersFilters = IPassengersQueryFilters & ISearchWithPaginationFilter;

export type ICreatePassengerRequest = {
  firstName: string;
  lastName: string;
  additionalPhone?: string;
  phone: string;
  addresses?: {
    base?: IPlace;
    occupation?: IPlace;
    alternate?: IPlace;
  };
  type: PassengerTypes;
  email: string;
  attributes?: number[];
  profile?: string;
  supplier?: string;
  membership?: string;
  isLimitedMobility?: boolean;
};

export type ICreateStudentRequest = {
  firstName: string;
  lastName: string;
  additionalPhone?: string;
  phone: string;
  addresses: {
    base: IPlace;
    occupation: IPlace;
    alternate?: IPlace;
  };
  type: PassengerTypes;
  email: string;
  profile?: string;
  attributes?: number[];
  gender?: GenderTypes;
  dateOfBirth?: number;
  acadmicYears?: string;
  parents?: number[];
  profiles?: string[];
  attendants?: number[];
  class: string;
  tags: Tag[];
  isLimitedMobility?: boolean;
};

export type ICreateEmployeeRequest = {
  firstName: string;
  lastName: string;
  additionalPhone?: string;
  phone: string;
  addresses: {
    base: IPlace;
    occupation: IPlace;
    alternate?: IPlace;
  };
  type: PassengerTypes;
  email: string;
  profile?: string;
  attributes?: number[];
  isLimitedMobility?: boolean;
};

export type ICreateCaregiverRequest = {
  firstName: string;
  lastName: string;
  phone: string;
  additionalPhone?: string;
  addresses: {
    base?: IPlace;
    occupation: IPlace;
    alternate?: IPlace;
  };
  type: PassengerTypes;
  email: string;
  profile?: string;
  attributes?: number[];
  attendants?: string[];
  isLimitedMobility?: boolean;
};

export type IUpdateEmployeeRequest = ICreateEmployeeRequest;

export type IUpdateStudentRequest = ICreateStudentRequest;

export type IUpdatePassengerRequest = ICreatePassengerRequest;

export type IUpdateCaregiverRequest = ICreateCaregiverRequest;

export type ISuspendPassengerRequest = {
  reason: string;
};

// ================= RESPONSE =================

export type ICreatePassengerResponse = {
  id: number;
};

export type IUpdatePassengerResponse = {
  id: number;
};

export type IGetSinglePassengerResponse = {
  id: number;
  type: PassengerTypes.Default;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  isLimitedMobility: boolean;
  phone: string;
  addresses?: PassengerAddressesResponse;
  tags?: Tag[];
  additionalPhone?: string;
  registerDate: string;
  registerWith: string;

  profile?: string;
  supplier?: string;
  attributes?: number[];

  suspension?: {
    isActive: boolean;
    reason: string;
  };

  membership?: string;
};

export type IGetSingleStudentResponse = {
  id: number;
  type: PassengerTypes.Student;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  isLimitedMobility: boolean;
  phone: string;
  additionalPhone?: string;
  registerWith: string;

  attributes?: number[];
  profile?: string;
  supplier?: string;

  suspension?: {
    isActive: boolean;
    reason: string;
  };

  parents?: number[];
  attendants?: number[];
  gender?: GenderTypes;
  dateOfBirth?: number;
  registerDate: string;

  tags?: Tag[];
  addresses: PassengerAddressesResponse;
};

export type IGetSingleEmployeeResponse = {
  id: number;
  type: PassengerTypes.Employee;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  isLimitedMobility: boolean;
  phone: string;
  addresses: PassengerAddressesResponse;
  tags?: Tag[];
  additionalPhone?: string;
  registerDate: string;
  registerWith: string;
  attributes?: number[];
  profile?: string;
  supplier?: string;
  suspension?: {
    isActive: boolean;
    reason: string;
  };
};

export type IGetSingleCaregiverResponse = {
  id: number;
  type: PassengerTypes.Caregiver;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  isLimitedMobility: boolean;
  phone: string;
  addresses?: PassengerAddressesResponse;
  tags?: Tag[];
  additionalPhone?: string;
  registerDate: string;
  registerWith: string;

  profile?: string;
  supplier?: string;
  attributes?: number[];
  attendants?: string[];

  suspension?: {
    isActive: boolean;
    reason: string;
  };
};

export type IGetSingleGeneralPassengerResponse =
  | IGetSinglePassengerResponse
  | IGetSingleEmployeeResponse
  | IGetSingleStudentResponse
  | IGetSingleCaregiverResponse;

export type IGetPassengersResponseBase = {
  id: number;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  phone: string;
  registerDate: string;
  profile?: string;
  supplier?: string;
  attributes?: string[];
  createdBy: string;
  createdAt: number;
  tags?: Tag[];
  isLimitedMobility: boolean;
};

export type INormalPassengersResponse = IGetPassengersResponseBase & {
  registerWith: string;
  occupationAddress?: string;
  baseAddress?: string;
  type: PassengerTypes.Default;
};

export type IGetStudentPassengersResponse = IGetPassengersResponseBase & {
  baseAddress?: string;
  alternateAddress?: string;
  occupationAddress?: string;
  type: PassengerTypes.Student;
};

export type IGetEmployeePassengersResponse = IGetPassengersResponseBase & {
  baseAddress?: string;
  occupationAddress: string;
  type: PassengerTypes.Employee;
};

export type IGetCaregiverPassengersResponse = IGetPassengersResponseBase & {
  baseAddress?: string;
  type: PassengerTypes.Caregiver;
};

export type IGetPassengersResponse = IPaginateResponse<
  INormalPassengersResponse | IGetStudentPassengersResponse | IGetEmployeePassengersResponse | IGetCaregiverPassengersResponse
>;

export type IDeletePassengerResponse = {
  msg: string;
  deleted: number;
};

export type IUpdatePassengerStatusResponse = {
  id: number;
};

export type IGetPassengersSimpleListResponse = IGetSimpleListResponse;

export type IGetPassengersSimpleListFilters =
  | IGetSimpleListFilters
  | {
      type: PassengerTypes;
      profile?: number;
      occupationLocationId?: number;
    };
